import HoursDisplay from 'components/util/HoursDisplay';
import MoneyDisplay from 'components/util/MoneyDisplay';
import { format, isSameDay } from 'date-fns';
import { ClassifierData } from 'types/classifiers';
import { TimecardListItemData } from 'types/timecards';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { dateStringToDateObject } from 'utils/parseDate';
import LabelledData from './LabelledData';
import TimecardListItem from './TimecardListItem';

export interface GroupedTimecards {
    loggedTimeSumInHours: number;
    billableTimeSumInHours: number;
    baseCurrency?: string;
    totalAmountInBaseCurrency: number;
    /**
     * Fee sums keyed by the rows' OWN currency — a group can hold timecards priced in several, and
     * the list's group ribbon lists one sum per currency. Never converted: a currency's sum only
     * ever comes from rows already priced in it, and `totalAmountInBaseCurrency` is the converted
     * total. Currencies with no billable fee are absent.
     */
    feesByCurrency: Map<string, number>;
    timecards: TimecardListItemData[];
}

type Props = {
    date: string;
    timecardGroup: GroupedTimecards;
    classifier: ClassifierData;
    compact?: boolean;
    classifiers: Record<string, ClassifierData>;
    borderless?: boolean;
};

const TimecardGroup = ({
    date,
    timecardGroup,
    classifier,
    classifiers,
    compact,
    borderless = false,
}: Props): JSX.Element => {
    const getGroupDate = () => {
        const dateObj = dateStringToDateObject(date);
        const isToday = isSameDay(dateObj, new Date());
        if (isToday) {
            return `${t('time.today')}, ${format(dateObj, t('timecards.list.group-date-format-without-day'))}`;
        } else {
            return format(dateObj, t('timecards.list.group-date-format'));
        }
    };

    return (
        <div
            className={cn(
                'bg-paper text-fg border-divider min-w-[600px] overflow-hidden',
                borderless ? 'border-t' : 'rounded-[4px] border',
                !compact && 'not-first-of-type:mt-6'
            )}
        >
            <div className="bg-grey-50 border-divider flex items-center border-b px-4 py-[9px]">
                <div className="text-h5 flex-auto">
                    <div className="flex w-full flex-wrap items-baseline justify-between">
                        <h6 className="text-subtitle2">{getGroupDate()}</h6>
                        <div className="flex gap-2">
                            <LabelledData label="timecards.list.total-time" className="font-semibold">
                                <HoursDisplay value={timecardGroup.loggedTimeSumInHours} />
                            </LabelledData>
                            <LabelledData label="timecards.list.billable-time" className="font-semibold">
                                <HoursDisplay value={timecardGroup.billableTimeSumInHours} />
                            </LabelledData>
                            <LabelledData label="timecards.list.total-amount" className="font-semibold">
                                <MoneyDisplay
                                    amount={timecardGroup.totalAmountInBaseCurrency}
                                    currency={timecardGroup.baseCurrency}
                                />
                            </LabelledData>
                        </div>
                    </div>
                </div>
            </div>
            <ul>
                {timecardGroup.timecards.map((timecard, index) => (
                    <li
                        key={timecard.id}
                        className={cn(
                            'relative flex w-full items-center',
                            index !== timecardGroup.timecards.length - 1 && 'border-divider border-b bg-clip-padding'
                        )}
                    >
                        <TimecardListItem
                            timecard={timecard}
                            classifier={classifier}
                            classifiers={classifiers}
                            compact={compact}
                        />
                    </li>
                ))}
            </ul>
        </div>
    );
};

export default TimecardGroup;
